import {
  ChangeDetectorRef,
  Component,
  OnInit,
  Input,
  SimpleChanges,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
} from "@angular/forms";
import {
  EST_WORK_TASKS,
  EST_EXPENDITURE_TYPES,
  EST_ACTIVITY_IDS,
  EST_CHARGE_TYPES,
  RESPONSIBILITY_CODES,
} from "../common/real-estate-form-options";

@Component({
  selector: "app-real-estate-project-estimate",
  templateUrl: "./real-estate-project-estimate.component.html",
  styleUrls: ["./real-estate-project-estimate.component.scss"],
})
export class RealEstateProjectEstimateComponent implements OnInit {
  @Input() reviewMode: boolean = false;
  @Input() realEstateEstimateForm: FormGroup;

  @Input() projectId: string;
  @Input() omOperatingUnits: string;
  @Input() capitalOperatingUnits: string;
  @Input() omAccountNum: string;
  @Input() capitalCwipAccountNum: string;
  @Input() capitalRwipAccountNum: string;
  @Input() state: string;
  @Input() serviceDate: Date;
  @Input() startDate: Date;
  private expenditureRecalcInProgress = false;

  // Options for dropdowns
  workTasks = EST_WORK_TASKS;
  expenditureTypes = EST_EXPENDITURE_TYPES;
  activityIds = EST_ACTIVITY_IDS;
  filteredActivityIds: [] = [];
  chargeTypes = EST_CHARGE_TYPES;
  respCenters = RESPONSIBILITY_CODES;

  displayRespCenter = (value: string): string => {
    const match = this.respCenters.find((c) => c.value === value);
    return match ? match.description : value || "";
  };

  filterRespCenters(value: string) {
    const term = (value || "").toString().toLowerCase();
    if (!term || this.respCenters.some((c) => c.value === value)) {
      return this.respCenters;
    }
    return this.respCenters.filter((c) =>
      c.description.toLowerCase().includes(term)
    );
  }

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    // Setup auto-sum for total of each charge row
    this.setupChargeRowAutoSum("labor");
    this.setupChargeRowAutoSum("contractor");
    this.setupChargeRowAutoSum("material");
    this.setupChargeRowAutoSum("other");
    this.setupChargeRowAutoSum("approved");
    // Setup auto-sum for estimates into charge summary
    this.setupEstimateAutoSum();
    // setup auto-sum for charge summary columns - approved funding
    this.setupApprovedFundingAutoSum();
    this.setupExpenditureSummarySubscription();
  }

  get estimatesArray(): FormArray {
    return this.realEstateEstimateForm.get("estimates") as FormArray;
  }

  get chargeSummaryGroup(): FormGroup {
    return this.realEstateEstimateForm.get("chargeSummary") as FormGroup;
  }

  get expenditureSummaryGroup(): FormGroup {
    return this.realEstateEstimateForm.get("expenditureSummary") as FormGroup;
  }

  addEstimate() {
    this.estimatesArray.push(
      this.fb.group({
        workTask: ["", Validators.required],
        expenditureType: ["", Validators.required],
        estimatedCost: ["", Validators.required],
        detailProjectId: [this.projectId], // Pre-filled with project ID
        activityId: ["", Validators.required],
        operatingUnitId: [""],
        processCode: [""],
        responsibilityCenter: ["", Validators.required],
        accountNumber: [""],
        chargeType: ["", Validators.required],
        projectChargeIndicator: [""],
      })
    );
    setTimeout(() => this.cdr.detectChanges());
  }

  deleteEstimate(index: number) {
    this.estimatesArray.removeAt(index);
  }

  // Filter activityIds based on expenditure type
  getFilteredActivityIds(expenditureType: string) {
    return EST_ACTIVITY_IDS.filter(
      (activity) => activity.expendType === expenditureType
    );
  }

  onExpenditureTypeChange(type: string, index: number) {
    const activity = this.estimatesArray.at(index).get("activityId").value;
    this.getOperatingUnitId(type, index);
    this.getAccountNum(type, activity, index);
  }

  onActivityIdChange(activityId: string, index: number) {
    const type = this.estimatesArray.at(index).get("expenditureType").value;
    this.getAccountNum(type, activityId, index);
    this.setProjectChargeIndicator(activityId, index);
  }

  getOperatingUnitId(expenditureType: string, index: number) {
    let ou: string = "";
    // Determine which operating unit to use based on expenditure type
    if (expenditureType === "Capital Exp") {
      ou = this.capitalOperatingUnits;
    } else if (expenditureType === "Operating Exp") {
      ou = this.omOperatingUnits;
    }

    if (ou && ou.length > 0) {
      const estimateGroup = this.estimatesArray.at(index) as FormGroup;
      estimateGroup.get("operatingUnitId").setValue(ou);
    }
  }

  getAccountNum(expenditureType: string, activityId: string, index: number) {
    let account: string = "";
    // Determine which operating unit to use based on expenditure type
    if (expenditureType === "Capital Exp") {
      if (activityId.includes("Removal")) {
        account = this.capitalRwipAccountNum;
      } else {
        account = this.capitalCwipAccountNum;
      }
    } else if (expenditureType === "Operating Exp") {
      account = this.omAccountNum;
    }

    if (account && account.length > 0) {
      const estimateGroup = this.estimatesArray.at(index) as FormGroup;
      estimateGroup.get("accountNumber").setValue(account);
    }
  }

  // Set Project Charge Indicator from activity ID
  setProjectChargeIndicator(activityId: string, index: number) {
    const activity = EST_ACTIVITY_IDS.find((a) => a.valueId === activityId);
    if (activity) {
      const estimateGroup = this.estimatesArray.at(index) as FormGroup;
      estimateGroup
        .get("projectChargeIndicator")
        .setValue(activity.chargeIndicator);
    }
  }

  // Sum estimates automatically when values change to populate charge summary
  // This will recalculate totals for each charge type
  // and update the charge summary accordingly
  private setupEstimateAutoSum() {
    this.estimatesArray.valueChanges.subscribe(() => {
      // For each charge type, recalculate totals
      ["labor", "contractor", "material", "other"].forEach((chargeType) => {
        this.sumEstimates(chargeType);
      });
      this.updateContingencyTotal();
    });
  }

  private sumEstimates(type: string) {
    let capitalAdditions = 0;
    let costOfRemoval = 0;
    let om = 0;

    this.estimatesArray.value.forEach((estimate) => {
      if (
        estimate.workTask === "Contingency" &&
        estimate.expenditureType === "Capital Exp"
      ) {
        return;
      }

      const expenditureType = estimate.expenditureType;
      const chargeType = estimate.chargeType;
      const activityId = estimate.activityId;
      const estimatedCost = estimate.estimatedCost;

      if (chargeType.toLowerCase() === type) {
        if (expenditureType === "Capital Exp") {
          if (activityId.includes("Removal")) {
            costOfRemoval += parseFloat(estimatedCost) || 0;
          } else {
            capitalAdditions += parseFloat(estimatedCost) || 0;
          }
        } else if (expenditureType === "Operating Exp") {
          om += parseFloat(estimatedCost) || 0;
        }
      }
    });

    this.chargeSummaryGroup
      .get(type + "CapitalAdditions")
      .setValue(capitalAdditions);
    this.chargeSummaryGroup.get(type + "CostOfRemoval").setValue(costOfRemoval);
    this.chargeSummaryGroup.get(type + "OM").setValue(om);
  }

  private updateContingencyTotal() {
    const contingencyTotal = this.estimatesArray.value.reduce(
      (total: number, estimate: any) => {
        if (
          estimate.workTask === "Contingency" &&
          estimate.expenditureType === "Capital Exp"
        ) {
          return total + (parseFloat(estimate.estimatedCost) || 0);
        }
        return total;
      },
      0
    );
    const contingencyControl = this.expenditureSummaryGroup.get(
      "totalEstContingency"
    );

    if (contingencyControl && Number(contingencyControl.value) !== contingencyTotal) {
      contingencyControl.setValue(contingencyTotal);
    }
  }

  // Sum charge type summary row totals automatically when values change
  // This will recalculate totals for each charge type
  private setupChargeRowAutoSum(type: string) {
    if (
      this.chargeSummaryGroup.get(type + "CapitalAdditions") &&
      this.chargeSummaryGroup.get(type + "CostOfRemoval") &&
      this.chargeSummaryGroup.get(type + "OM") &&
      this.chargeSummaryGroup.get(type + "Total")
    ) {
      this.chargeSummaryGroup.valueChanges.subscribe(() => {
        this.updateChargeRowTotal(type);
      });
    }
  }

  private updateChargeRowTotal(type: string) {
    const additions =
      parseFloat(
        this.chargeSummaryGroup.get(type + "CapitalAdditions").value
      ) || 0;
    const removal =
      parseFloat(this.chargeSummaryGroup.get(type + "CostOfRemoval").value) ||
      0;
    const om = parseFloat(this.chargeSummaryGroup.get(type + "OM").value) || 0;
    const totalCtrl = this.chargeSummaryGroup.get(type + "Total");
    const sum = additions + removal + om;

    if (totalCtrl) {
      totalCtrl.setValue(sum, { emitEvent: false });
    }
  }

  // Sum approved funding totals when charge summary values change
  private setupApprovedFundingAutoSum() {
      this.chargeSummaryGroup.valueChanges.subscribe(() => {
      // Recalculate approved row aggregates (Labor + Contractor + Material + Other)
      ["CapitalAdditions", "CostOfRemoval", "OM"].forEach((chargeCategory) => {
        this.updateApprovedRowTotal(chargeCategory);
      });

      // Read approved totals from charge summary (upper table)
      const approvedCapitalAdditions =
        parseFloat(this.chargeSummaryGroup.get("approvedCapitalAdditions").value) || 0;
      const approvedCostOfRemoval =
        parseFloat(this.chargeSummaryGroup.get("approvedCostOfRemoval").value) || 0;
      const approvedOM =
        parseFloat(this.chargeSummaryGroup.get("approvedOM").value) || 0;

      // Compute approved capital cost and approved grand total
      const approvedCapCost = approvedCapitalAdditions + approvedCostOfRemoval;
      const approvedTotal = approvedCapCost + approvedOM;

      // The parent component's split logic will derive 'current' and 'previous' from this.
      // Setting emitEvent: true ensures the parent listener is triggered.
      this.expenditureSummaryGroup
        .get("totalEstCapCost")
        .setValue(approvedCapCost, { emitEvent: true });

      this.expenditureSummaryGroup
        .get("totalEstOM")
        .setValue(approvedOM, { emitEvent: true });

      // Calculate AFUDC now so first submit has full summary values populated
      this.calculateAFUDCTotals(true);

      const totalAfudc =
        parseFloat(this.expenditureSummaryGroup.get("totalEstAFUDC").value) || 0;
      const totalContingency =
        parseFloat(
          this.expenditureSummaryGroup.get("totalEstContingency").value
        ) || 0;

      // Populate the remaining total project funding fields immediately
      this.expenditureSummaryGroup
        .get("totalTotalCapital")
        .setValue(approvedCapCost + totalAfudc, { emitEvent: false });

      this.expenditureSummaryGroup
        .get("totalTotalNoAFUDC")
        .setValue(approvedCapCost + approvedOM + totalContingency, {
          emitEvent: false,
        });

      this.expenditureSummaryGroup
        .get("totalTotalWithAFUDC")
        .setValue(approvedCapCost + approvedOM + totalAfudc + totalContingency, {
          emitEvent: true,
        });

      // Keep charge summary approved total in sync
      const approvedTotalCtrl = this.chargeSummaryGroup.get("approvedTotal");
      if (approvedTotalCtrl) {
        approvedTotalCtrl.setValue(approvedTotal, { emitEvent: false });
      }
    });
  }

  updateApprovedRowTotal(chargeCategory: string) {
    const laborCharge =
      parseFloat(this.chargeSummaryGroup.get("labor" + chargeCategory).value) ||
      0;
    const materialCharge =
      parseFloat(
        this.chargeSummaryGroup.get("material" + chargeCategory).value
      ) || 0;
    const contractorCharge =
      parseFloat(
        this.chargeSummaryGroup.get("contractor" + chargeCategory).value
      ) || 0;
    const otherCharge =
      parseFloat(this.chargeSummaryGroup.get("other" + chargeCategory).value) ||
      0;
    const approvedCtrl = this.chargeSummaryGroup.get(
      "approved" + chargeCategory
    );

    const sum = laborCharge + materialCharge + contractorCharge + otherCharge;

    if (approvedCtrl) {
      approvedCtrl.setValue(sum, { emitEvent: false });
    }
  }

  setupExpenditureSummarySubscription() {
    this.expenditureSummaryGroup.valueChanges.subscribe(() => {

      // Prevent recursive valueChanges loop while patching calculated fields
      if (this.expenditureRecalcInProgress) {
        return;
      }

      this.expenditureRecalcInProgress = true;
      try {
        // Calculate AFUDC totals when form values change
        this.calculateAFUDCTotals();

        // Read the latest values needed for calculation
        const totalCapCost =
          parseFloat(this.expenditureSummaryGroup.get("totalEstCapCost").value) || 0;
        const totalEstOM =
          parseFloat(this.expenditureSummaryGroup.get("totalEstOM").value) || 0;
        const totalAfudc =
          parseFloat(this.expenditureSummaryGroup.get("totalEstAFUDC").value) || 0;
        const totalContingency =
          parseFloat(
            this.expenditureSummaryGroup.get("totalEstContingency").value
          ) || 0;

        // Calculate the grand totals
        const totalCapital = totalCapCost + totalAfudc;
        const totalFundingNoAFUDC = totalCapCost + totalEstOM + totalContingency;
        const totalFundingWithAFUDC =
          totalCapCost + totalEstOM + totalAfudc + totalContingency;

        // These are the final calculations in this component's chain.
        // The parent's listener on the top-level form will catch the change regardless.
        this.expenditureSummaryGroup
          .get("totalTotalCapital")
          .setValue(totalCapital, { emitEvent: false });

        this.expenditureSummaryGroup
          .get("totalTotalNoAFUDC")
          .setValue(totalFundingNoAFUDC, { emitEvent: false });

        this.expenditureSummaryGroup
          .get("totalTotalWithAFUDC")
          .setValue(totalFundingWithAFUDC, { emitEvent: false });
      } finally {
        this.expenditureRecalcInProgress = false;
      }
    });
  }

  // update summary expenditure total funding based on current and previous funding
  updateTotalFunding(summaryType: string) {
    const previous =
      parseFloat(
        this.expenditureSummaryGroup.get("previous" + summaryType).value
      ) || 0;
    const current =
      parseFloat(
        this.expenditureSummaryGroup.get("current" + summaryType).value
      ) || 0;
    const totalCtrl = this.expenditureSummaryGroup.get("total" + summaryType);

    const sum = previous + current;

    if (totalCtrl) {
      totalCtrl.setValue(sum, { emitEvent: false });
    }
  }

  // Calculate EFUDC totals based on state and business logic
  calculateAFUDCTotals(emitEvent: boolean = false) {
    let afudc = 0;
    
    // Normalize input values to Date without adding a new method
    const service =
      this.serviceDate instanceof Date
        ? this.serviceDate
        : new Date(this.serviceDate as any);
    const start =
      this.startDate instanceof Date
        ? this.startDate
        : new Date(this.startDate as any);

    if (
      !service ||
      !start ||
      isNaN(service.getTime()) ||
      isNaN(start.getTime())
    ) {
      return;
    }

    // Calculate AFUDC totals using the given logic provided (below)
    // IF(state<>"FL", SUM(capital additions+capital - cost of removal+previously approved/2)(0.061/12)*estimated in-service date-start date,FALSE))
    if (this.state === "FL") {
      // Florida: AFUDC always zero
      this.expenditureSummaryGroup
        .get("totalEstAFUDC")
        .setValue(0, { emitEvent: emitEvent });
      return;
    }
      // Read from TOTAL fields for calculation
      const totalCapCost = Number(this.expenditureSummaryGroup.get("totalEstCapCost").value) || 0;
      // The formula should use the full capital cost, not a mix of current and previous
      afudc =
        (totalCapCost / 2) *
        (0.061 / 12) *
        Math.round(
          (service.getTime() - start.getTime()) /
            (1000 * 60 * 60 * 24 * 30)
        );
      afudc = Math.floor(afudc);

    // If AFUDC is calculated, set it in the form
    this.expenditureSummaryGroup
      .get("totalEstAFUDC")
      .setValue(afudc, { emitEvent: emitEvent });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projectId && changes.projectId.currentValue) {
      // If the form is initialized and no estimates exist, add one with the new projectId
      if (this.realEstateEstimateForm && this.estimatesArray.length === 0) {
        this.addEstimate();
      }
      // Update all existing estimates with the new projectId:
      this.estimatesArray.controls.forEach((group) => {
        group.get("detailProjectId").setValue(this.projectId);
      });
    }

    // Handle reviewMode changes
    if (this.realEstateEstimateForm && changes["reviewMode"]) {
      if (this.reviewMode) {
        this.realEstateEstimateForm.disable();
      } else {
        this.realEstateEstimateForm.enable();
      }
    }

    // Recalculate AFUDC when one of its calculation inputs changes
    if (
      this.realEstateEstimateForm &&
      (changes["startDate"] || changes["serviceDate"] || changes["state"])
    ) {
      this.calculateAFUDCTotals(true);
    }
  }
}
